/* import __COLOCATED_TEMPLATE__ from './manage-devices-modal.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import type Snackbar from 'embercom/services/snackbar';
import { request, deleteRequest, ResponseError } from 'embercom/lib/inbox/requests';
import type PermissionsModal from 'embercom/services/permissions-modal';

interface DeviceTokenWire {
  type: string;
  id: string;
  token: string;
  platform: string;
  phone_number: string | null;
  device_id: string | null;
  created_at: number;
  updated_at: number;
}

interface DeviceToken {
  id: string;
  token: string;
  phoneNumber: string | null;
  platform: string;
  createdAt: string;
  isDisabling: boolean;
}

interface Args {
  isOpen: boolean;
  userName: string;
  userId?: string;
  onClose: () => void;
  onDevicesChanged?: () => void;
}

interface Signature {
  Args: Args;
}

const PLATFORM_SLUG_MAP: Record<string, string> = {
  'iOS Production': 'ios',
  'iOS Development': 'ios-dev',
  Android: 'android',
  Chrome: 'chrome',
  Firefox: 'firefox',
};

function platformSlug(apiPlatform: string): string {
  return PLATFORM_SLUG_MAP[apiPlatform] ?? 'unknown';
}

function deserializeDeviceToken(wire: DeviceTokenWire): DeviceToken {
  return {
    id: wire.id,
    token: wire.token,
    phoneNumber: wire.phone_number,
    platform: platformSlug(wire.platform),
    createdAt: new Date(wire.created_at * 1000).toLocaleDateString(),
    isDisabling: false,
  };
}

export default class ManageDevicesModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare permissionsModal: PermissionsModal;
  @service declare snackbar: Snackbar;
  @service declare session: Session;

  @tracked devices: DeviceToken[] = [];
  @tracked isLoading = false;
  @tracked isLoadError = false;
  @tracked confirmDisableDevice: DeviceToken | null = null;

  get title(): string {
    return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.modal-title', {
      userName: this.args.userName,
    });
  }

  get hasDevices(): boolean {
    return this.devices.length > 0;
  }

  get isConfirmDisableDeviceOpen(): boolean {
    return this.confirmDisableDevice !== null;
  }

  @action platformLabel(platform: string): string {
    switch (platform) {
      case 'ios':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-ios');
      case 'ios-dev':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-ios-dev');
      case 'android':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-android');
      case 'chrome':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-chrome');
      case 'firefox':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-firefox');
      default:
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-unknown');
    }
  }

  @action async loadDevices() {
    this.devices = [];
    this.isLoading = true;
    this.isLoadError = false;
    try {
      let response = await request(
        `/ember/users/${this.args.userId}/device_tokens?app_id=${this.session.workspace.id}`,
      );
      let json: { type: string; data: DeviceTokenWire[] } = await response.json();
      this.devices = json.data.map(deserializeDeviceToken);
    } catch (e: unknown) {
      this.isLoadError = true;
      this.devices = [];

      if (e instanceof ResponseError && e.response.status === 403) {
        let json = await e.response.json();
        if (json?.required_permission) {
          this.permissionsModal.showModal({ permissionsLabel: json.required_permission });
          return;
        }
      }
    } finally {
      this.isLoading = false;
    }
  }

  @action promptDisableDevice(device: DeviceToken) {
    this.confirmDisableDevice = device;
  }

  @action cancelDisableDevice() {
    this.confirmDisableDevice = null;
  }

  @action async confirmAndDisableDevice() {
    let device = this.confirmDisableDevice;
    if (!device) {
      return;
    }
    this.confirmDisableDevice = null;

    let deviceIndex = this.devices.indexOf(device);
    if (deviceIndex === -1) {
      return;
    }

    this.devices = this.devices.map((d, i) =>
      i === deviceIndex ? { ...d, isDisabling: true } : d,
    );

    let deviceId = device.id;

    try {
      await deleteRequest(
        `/ember/users/${this.args.userId}/device_tokens/${device.id}?app_id=${this.session.workspace.id}`,
      );
      this.devices = this.devices.filter((d) => d.id !== deviceId);
      this.snackbar.notify(
        this.intl.t('inbox.attribute-list-item.user-data-menu.devices.disable-success', {
          userName: this.args.userName,
        }),
      );
      this.args.onDevicesChanged?.();
    } catch (e: unknown) {
      this.devices = this.devices.map((d) =>
        d.id === deviceId ? { ...d, isDisabling: false } : d,
      );

      if (e instanceof ResponseError && e.response.status === 403) {
        let json = await e.response.json();
        if (json?.required_permission) {
          this.permissionsModal.showModal({ permissionsLabel: json.required_permission });
          return;
        }
      }

      this.snackbar.notify(
        this.intl.t('inbox.attribute-list-item.user-data-menu.devices.disable-failure', {
          userName: this.args.userName,
        }),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::ManageDevicesModal': typeof ManageDevicesModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/manage-devices-modal': typeof ManageDevicesModal;
  }
}
